var AbsoluteShowingQueueSupport = require("enyo-perf/AbsoluteShowingQueueSupport"),
    Control                     = require("enyo/Control"),
    PerformanceMode             = require("enyo-perf/PerformanceMode"),
    PerformanceModeQueueSupport = require("enyo-perf/PerformanceModeQueueSupport"),
    SimpleRepeater,
    kind                        = require("enyo/kind"),
    utils                       = require("enyo/utils");

require("enyo-ui");

/**
 * [SimpleRepeater]{@link enyo-ui.SimpleRepeater} provides the infrastructure to
 * create and render child controls for each record in a dataset. Unlike
 * [Repeater]{@link enyo-ui.SimpleRepeater} it takes no responsibility of how
 * the child controls are arranged.
 *
 * It needs an adapter for the particular type of data it will receive - such as
 * [RepeaterArraySupport]{@link enyo-ui.RepeaterArraySupport}, for array
 * data.
 *
 * @ui
 * @class   enyo-ui.SimpleRepeater
 * @extends enyo.Control
 * @mixes   enyo-perf.AbsoluteShowingQueueSupport
 * @mixes   enyo-perf.PerformanceModeQueueSupport
 * @public
 */
SimpleRepeater = module.exports = kind(/** @lends enyo-ui.SimpleRepeater.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.SimpleRepeater",

    /**
     * @private
     */
    kind: Control,

    /**
     * @private
     */
    mixins: [
        AbsoluteShowingQueueSupport,
        PerformanceModeQueueSupport
    ],

    /**
     * @private
     */
    classes: "enyo-ui-simple-repeater",

    /**
     * The kind responsible for implementing the way the repeater interacts
     * with it's dataset.
     *
     * @type {enyo-ui.RepeaterDataAdapter}
     * @public
     */
    dataAdapterKind: null,

    /**
     * The kind that will be rendered for each item in the dataset.
     *
     * @type {enyo.Control}
     * @public
     */
    childKind: null,

    /**
     * Optional extra options to merge into the child kind at creation time.
     *
     * @type {Object=}
     * @public
     */
    childOptions: null,

    /**
     * Whether the drawing process should be asynchronous, drawing one child
     * at at time.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    drawAsync: false,

    /**
     * The dataset feeding this repeater.
     *
     * @type {*}
     * @public
     */
    data: null,

    /**
     * The length of the dataset.
     *
     * @type {number}
     * @readonly
     * @public
     */
    length: 0,

    /**
     * This hash maps the unique ids of records in the dataset to child
     * controls. This mapping will be updated any time a child is created or
     * removed.
     *
     * @type {Object}
     * @private
     */
    recordUidToChild: null,

    /**
     * The index of the last drawn child
     *
     * @type {?number}
     * @private
     */
    lastDrawnIndex: null,

    /**
     * A flag that tells us if the repeater is currently drawing its children.
     *
     * @type    {boolean}
     * @default false
     * @public
     */
    drawing: false,

    // Initialization
    // --------------------------------

    /**
     * @method
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            this.recordUidToChild = {};
            sup.apply(this, arguments);
        };
    }),

    /**
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.maybeDrawNext = this.bindSafely(this.maybeDrawNext);
            sup.apply(this, arguments);
            this.createDataAdapter();
        };
    }),

    /**
     * Creates an adapter for handling the dataset passed to this repeater.
     *
     * @private
     */
    createDataAdapter: function() {
        if (!this.dataAdapterKind) {
            this.error("Data Adapter required.");
        }
        this.dataAdapter = kind.createFromKind(this.dataAdapterKind, this);
    },

    /**
     * Returns the data adapter.
     *
     * @returns  {?Component}  The data adapter, if available.
     * @public
     */
    getDataAdapter: function() {
        return this.dataAdapter || null;
    },

    // Destruction
    // --------------------------------

    /**
     * @method
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            var dataAdapter = this.dataAdapter;

            dataAdapter && dataAdapter.destroy && dataAdapter.destroy();
            sup.apply(this, arguments);
        };
    }),

    // Dataset
    // --------------------------------

    /**
     * Gets a record from the dataset by index.
     *
     * @param  {number}  index  The index
     * @return {*}       The record, or `null` if not found.
     * @abstract
     * @protected
     */
    getRecordByIndex: function(index) {
        return this.dataAdapter.getRecordByIndex(index);
    },

    /**
     * Gets a record from the dataset by index.
     *
     * @param  {*}       record  The record
     * @return {number}  The index, or `null` if not found.
     * @private
     */
    getIndexByRecord: function(record) {
        return this.dataAdapter.getIndexByRecord(record);
    },

    /**
     * Retrieves a unique identifier from the record.
     *
     * @param  {*}       record  The record
     * @return {string}  The record uid.
     * @abstract
     * @protected
     */
    getRecordUid: function(record) {
        return this.dataAdapter.getRecordUid(record);
    },

    // Children
    // --------------------------------

    /**
     * Creates (but doesn't render) a child control for the provided record.
     *
     * @param  {*}             record  The record
     * @return {enyo.Control}  The created child
     * @protected
     */
    createChild: function(record) {
        var recordUid        = this.getRecordUid(record),
            recordUidToChild = this.recordUidToChild,
            childKind        = this.getChildKind(record),
            childOptions     = this.getChildOptions(childKind, record),
            child;

        // Child already exists
        if (recordUidToChild[recordUid]) {
            this.error("Child for record: " + recordUid + " already exists.");

            return recordUidToChild[recordUid];
        }

        // Create child configured not to render immediately
        child = this.createComponent({
            name:        "repeater_child_" + recordUid,
            kind:        childKind,
            classes:     "enyo-ui-simple-repeater__child " + (childOptions.classes || ""),
            canGenerate: false,
            data:        record,
            repeater:    this
        }, childOptions);

        // Index child
        recordUidToChild[recordUid] = child;

        return child;
    },

    /**
     * Override this method if the returned kind needs to change based on the
     * record it'll show.
     *
     * @param  {Object=}   record The record
     * @return {Function}  The kind constructor
     * @protected
     */
    getChildKind: function() {
        return this.childKind;
    },

    /**
     * Override this method if the returned options need to change based on the
     * kind that will be used.
     *
     * @param  {Function}  kind   The childKind
     * @param  {Object=}   record The record
     * @return {?Object}   The options
     * @protected
     */
    getChildOptions: function() {
        return this.childOptions || {};
    },

    /**
     * Gets the child associated with the record at the provided index in the
     * dataset.
     *
     * @param  {number}         index  The index
     * @return {?enyo.Control}  The child if found, `null` otherwise.
     * @public
     */
    getChildForIndex: function(index) {
        var record,
            recordUid;

        if (!this.length) {
            return null;
        }

        record    = this.getRecordByIndex(index);

        if (!record) {
            return null;
        }

        recordUid = this.getRecordUid(record);

        if (!recordUid) {
            return null;
        }

        return this.recordUidToChild[recordUid] || null;
    },

    /**
     * Makes sure the child is removed from the arrangement, no longer mapped to
     * it's record and destroyed.
     *
     * @param {Object}  record  The record
     * @protected
     */
    destroyChild: function(record) {
        var recordUid = this.getRecordUid(record),
            child     = this.recordUidToChild[recordUid];

        if (child) {
            delete this.recordUidToChild[recordUid];
            child.destroy();
        }
    },

    /**
     * Iterates over indexed records and removes the corresponding children.
     *
     * @private
     */
    destroyChildren: function() {
        var recordUidToChild = this.recordUidToChild,
            recordUid;

        this.stopDrawing();

        for (recordUid in recordUidToChild) {
            if (recordUidToChild.hasOwnProperty(recordUid)) {
                this.destroyChild(recordUidToChild[recordUid].data);
            }
        }
        this.lastDrawnIndex = null;
    },

    // Render, Resize, Reflow
    // --------------------------------

    /**
     * Interrupt drawing if needed. Remove draw from absoluteShowing queue to
     * prevent it from getting called twice ([rendered]{@link
     * enyo-ui.SimpleRepeater#rendered} will call it explicitly).
     *
     * @method
     * @private
     */
    render: kind.inherit(function(sup) {
        return function() {
            this.stopDrawing();
            sup.apply(this, arguments);
        };
    }),

    /**
     * Children are drawn after the repeater has rendered. At this point the
     * arranger will have already decided the size of the arrangement and
     * applied it to the DOM, which is all we really need for Enyo's regular
     * render flow.
     *
     * @method
     * @private
     */
    rendered: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.maybeDraw();
        };
    }),

    // Draw
    // --------------------------------

    /**
     * Marks the start of a draw cycle. Internal use only.
     *
     * @private
     */
    startDrawing: function() {
        if (!this.drawing) {
            this.set("drawing", true);
            this.set("needsDraw", false);
        }
    },

    /**
     * Checks if we should defer drawing due to performance mode or if the
     * repeater isn't absoluteShowing. If deferred, we'll call `draw()` once
     * conditions are right, otherwise we'll call it immediately.
     *
     * @private
     */
    maybeDraw: function() {
        // Defer if not showing
        if (!this.absoluteShowing && this.shouldDeferDrawWhenNotAbsoluteShowing()) {
            this.addToAbsoluteShowingQueue("maybeDraw");

            return;
        }
        this.removeFromAbsoluteShowingQueue("maybeDraw");

        // Defer when in performanceMode
        if (PerformanceMode.on && this.shouldDeferDrawInPerformanceMode()) {
            this.addToPerformanceModeQueue("maybeDraw");

            return;
        }
        this.removeFromPerformanceModeQueue("maybeDraw");

        this.draw();
    },

    /**
     * Draw children for each record in the dataset. This method shouldn't be
     * called directly. Call `maybeDraw()` instead.
     *
     * @protected
     */
    draw: function() {
        // No data, nothing to draw
        if (!this.data || !this.data.length || this.data.length < 0) {
            this.stopDrawing();

            return;
        }

        // Start draw loop if not started yet.
        this.startDrawing();

        // Get indexes that should be arranged.
        this.lastDrawnIndex = -1;

        // Start drawing children for individual indexes.
        this.drawAsync ? this.requestAsyncDrawNext() : this.maybeDrawNext();
    },

    /**
     * Stops any currently active draw loop.
     *
     * @public
     */
    stopDrawing: function() {
        if (this.drawing) {
            this.removeFromAbsoluteShowingQueue("maybeDraw");
            this.removeFromPerformanceModeQueue("maybeDraw");
            this.removeFromAbsoluteShowingQueue("maybeDrawNext");
            this.removeFromPerformanceModeQueue("maybeDrawNext");
            this.cancelAsyncDrawNext();
            this.set("drawing", false);
        }
    },

    /**
     * Continues any previously interrupted draw loop.
     *
     * @public
     */
    continueDrawing: function() {
        if (this.data
            && this.data.length > 0
            && this.lastDrawnIndex < this.data.length - 1) {
            this.set("drawing", true);
            this.drawAsync ? this.requestAsyncDrawNext() : this.maybeDrawNext();
        }
    },

    /**
     * Checks if we should defer drawing the next child due to performance mode
     * or if the repeater isn't absoluteShowing. If deferred, we'll call
     * drawNext() once conditions are right, otherwise we'll call it
     * immediately.
     *
     * @private
     */
    maybeDrawNext: function() {
        // Defer if not showing
        if (!this.absoluteShowing && this.shouldDeferDrawWhenNotAbsoluteShowing()) {
            this.addToAbsoluteShowingQueue("maybeDrawNext");

            return;
        }
        this.removeFromAbsoluteShowingQueue("maybeDrawNext");

        // Defer when in performanceMode
        if (PerformanceMode.on && this.shouldDeferDrawInPerformanceMode()) {
            this.addToPerformanceModeQueue("maybeDrawNext");

            return;
        }
        this.removeFromPerformanceModeQueue("maybeDrawNext");

        this.drawNext();
    },

    /**
     * Evaluates if there are any undrawn indexes. If so, an index is popped off
     * the stack and a child control created and rendered as needed.
     *
     * This method will be deferred if the repeater is not absolutely showing or
     * if the application is in performance mode.
     *
     * @protected
     */
    drawNext: function() {
        var drawAsync      = this.drawAsync,
            lastDrawnIndex = this.lastDrawnIndex,
            index,
            record,
            child;

        if (this.drawTimeout) {
            this.drawTimeout = null;
        }

        // No undrawn indexes, we're done
        if (!this.data
            || !this.data.length
            || lastDrawnIndex === this.data.length - 1) {
            this.stopDrawing();

            return;
        }

        // Ensure a child for the next index
        index  = ++lastDrawnIndex;
        record = this.getRecordByIndex(index);

        // Only draw child if record is found
        if (record) {
            child = this.getChildForIndex(index);
            child = child ? this.updateChild(child, record) : this.createChild(record);
            child && this.drawChild(child);
        }

        // We'll still skip the index if the record was not found.
        this.lastDrawnIndex = index;

        // Schedule next draw
        drawAsync ? this.requestAsyncDrawNext() : this.maybeDrawNext();
    },

    /**
     * By default we don't draw if not absoluteShowing. Override if another
     * strategy is desired.
     *
     * @returns {bool} Whether to defer drawing the next child.
     * @protected
     */
    shouldDeferDrawWhenNotAbsoluteShowing: function() {
        return true;
    },

    /**
     * By default we don't draw in PerformanceMode. Override if another strategy
     * is desired.
     *
     * @returns {bool} Whether to defer drawing the next child.
     * @protected
     */
    shouldDeferDrawInPerformanceMode: function() {
        return false;
    },

    /**
     * Schedules maybeDrawNext() call to be executed asynchronously.
     *
     * @protected
     */
    requestAsyncDrawNext: function() {
        if (!this.drawTimeout) {
            this.drawTimeout = setTimeout(this.maybeDrawNext, 0);
        }
    },

    /**
     * Cancels the scheduled maybeDrawNext() call.
     *
     * @protected
     */
    cancelAsyncDrawNext: function() {
        this.drawTimeout && clearTimeout(this.drawTimeout);
        this.drawTimeout = null;
    },

    /**
     * This method can be used to update the dataset of a child right before
     * it's drawn.
     *
     * @param  {enyo.Control}  child   The child
     * @param  {*}             record  The record
     * @return {enyo.Control}  The created child
     * @public
     */
    updateChild: function(child, record) {
        return this.dataAdapter.updateChild(child, record);
    },

    /**
     * Draws a child.
     *
     * @param  {enyo.Control}  child  The child
     * @private
     */
    drawChild: function(child) {
        if (this.hasNode() && !child.hasNode()) {
            child.canGenerate = true;
            child.render();
        }
    }

});

/**
 * This method allows enyo.kind to extend the `childOptions` hash when creating
 * super kinds.
 *
 * @param    {Function}  ctor   The constructor
 * @param    {Object}    props  The properties
 * @memberof Repeater
 * @static
 * @private
 */
SimpleRepeater.concat = function(ctor, props) {
    var proto = ctor.prototype || ctor,
        childOptions;

    if (props.childOptions) {
        childOptions       = proto.childOptions ? utils.clone(proto.childOptions) : {};
        proto.childOptions = utils.mixin(childOptions, props.childOptions);
        delete props.childOptions;
    }
};
